import { getHierarchyNames } from '../getHierarchyNames';

export interface CategoryHierarchy {
  id: string;
  breadcrumbs: Array<{ label: string }>;
}

/**
 * Builds category hierarchy breadcrumbs from a flat array of categories
 * Generic utility that works for both venue categories and product categories
 *
 * @template T - Type that must have a name property
 * @param {T[]} categories - Array of categories to build hierarchies for
 * @param {T[]} allCategories - Complete flat array containing all categories in the hierarchy
 * @param {Object} config - Configuration for mapping properties
 * @param {function} config.getId - Function to extract the id from a category
 * @param {function} config.getParentId - Function to extract the parent id from a category
 * @param {keyof T} config.idKey - Property name for id in allCategories array
 * @param {keyof T} config.parentIdKey - Property name for parent_id in allCategories array
 * @returns {CategoryHierarchy[]} Array of category hierarchies with breadcrumbs
 *
 * @example
 * // For venue categories
 * buildCategoryHierarchy(
 *   venueCategories,
 *   allVenueCategories,
 *   {
 *     getId: (cat) => cat.categoryId,
 *     getParentId: (cat) => cat.parentCategoryId,
 *     idKey: 'category_id',
 *     parentIdKey: 'parent_category_id'
 *   }
 * )
 */
export const buildCategoryHierarchy = <T extends { name: string }, U extends { name: string }>(
  categories: T[],
  allCategories: U[],
  config: {
    getId: (category: T) => string;
    getParentId: (category: T) => string | null;
    idKey: keyof U;
    parentIdKey: keyof U;
  },
): CategoryHierarchy[] => {
  if (!categories.length || !allCategories.length) {
    return [];
  }

  return categories.map((category) => {
    const categoryId = config.getId(category);
    const fullCategory = allCategories.find((cat) => cat[config.idKey] === categoryId);

    if (!fullCategory) {
      return {
        id: categoryId,
        breadcrumbs: [{ label: category.name }],
      };
    }

    const hierarchyNames = getHierarchyNames(fullCategory, allCategories, {
      id: config.idKey,
      parent_id: config.parentIdKey,
    });

    return {
      id: categoryId,
      breadcrumbs: hierarchyNames.map((name) => ({ label: name })),
    };
  });
};
